import { forwardRef, HTMLAttributes, useCallback, useContext, useState } from "react";
import { useIntl } from "react-intl";
import {
  autoUpdate,
  FloatingPortal,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from "@floating-ui/react";
import { IconUsers } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { AgeCategory } from "@shared/graphql";
import { actions, common, form } from "../../../../i18n/sharable-defs";
import { StickableContext } from "../../../layout/wrapper";
import Button from "../../../common/button";
import GuestsSelector from "../../../inputs/guests";
import SpriteSvg from "../../../../components/common/svg";
import "./guests.scss";

type ResumeProps = HTMLAttributes<HTMLDivElement> & Pick<Props, "value">;

type Props = {
  maxGuests?: number;
  value?: Record<AgeCategory, number>;
  onChange?: (value?: Record<AgeCategory, number>) => void;
}

const Resume = forwardRef<HTMLDivElement, ResumeProps>(({ className, value, ...props }, forwardRef) => {

  const intl = useIntl();

  const format = useCallback((guests?: Record<AgeCategory, number>): string => {
    const count = Object.values(guests || {}).reduce((acc, val) => acc + val, 0);
    return count > 0 ? intl.formatMessage(common.countedGuests, { count }) : "";
  }, [ value, intl ]);

  const wrapperClasses = classNames("participants-input", "fi-group", className);

  return (
    <div ref={forwardRef}
         className={wrapperClasses}
         {...props}>
      <SpriteSvg id="person"/>
      <label htmlFor="participants">{intl.formatMessage(form.guests)}</label>
      <input type="text"
             id="participants"
             placeholder={intl.formatMessage(form.guestsPlaceholder)}
             value={format(value)}
             readOnly={true} />
    </div>);

});

const GuestsInput = ({ maxGuests, value, onChange = () => void 0 }: Props) => {

  const intl = useIntl();
  const { sticked } = useContext(StickableContext);

  const [ pickerOpen, setPickerOpen ] = useState<boolean>(false);

  const { refs, context, floatingStyles } = useFloating({
    open: pickerOpen,
    onOpenChange: setPickerOpen,
    strategy: "absolute",
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [
      size({
        apply: ({ rects, elements }) => {
          elements.floating.style.minWidth = `${rects.reference.width}px`;
        }
      }),
      offset(({ rects, elements }) => {
        elements.floating.style.setProperty("--ccv-interest-display-width", `${rects.reference.width}px`);
        return -1 * rects.reference.height;
      })
    ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { toggle: false }),
    useDismiss(context, { outsidePress: true })
  ]);

  if (sticked)
    return <GuestsSelector max={maxGuests} value={value} onChange={onChange} />;

  return (
    <>
      <Resume ref={refs.setReference}
              {...getReferenceProps()}
              value={value} />
      {pickerOpen &&
        <FloatingPortal>
          <div ref={refs.setFloating}
               {...getFloatingProps()}
               style={floatingStyles}
               className="vars-for-portal picker-wrap participants">
            <div className="picker-header">
              <Resume value={value} />
            </div>
            <div className="participants-editor">
              <GuestsSelector max={maxGuests} value={value} onChange={onChange} />
            </div>
            <div className="picker-footer">
              <Button className="primary" onClick={() => setPickerOpen(false)}>{intl.formatMessage(actions.close)}</Button>
            </div>
          </div>
        </FloatingPortal>
      }
    </>);

};

export default GuestsInput;
